import { useCallback, useEffect, useRef, useState } from "react";
import {
  useLazySyncWorkItemCalendarEventsQuery,
  WorkItemSyncResult,
} from "../../../store/ms-services/calendarDeltaApi";
import {
  useLazyGetMsGraphDeltaKeyQuery,
  usePostMsGraphDeltaKeyMutation,
} from "../../../store/services/userService";
import { useUpdateWorkItemsFromCalendarMutation } from "../../../store/services/workItemService";
import { baseApi } from "../../../store/api/baseApi";
import { useDispatch } from "react-redux";
import msBaseApi from "../../../store/api/msBaseApi";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";

// Konstanter för tydlighet och konfigurationshantering
const SYNC_THROTTLE_MS = 15000; // 10 sekunder
const SYNC_INTERVAL_MS = 5 * 60 * 1000; // 5 minuter
interface SyncStatusResult {
  isLoading: boolean;
  error: FetchBaseQueryError | SerializedError | undefined;
  lastSyncResult: WorkItemSyncResult | null;
  lastServerSyncResult: ServerSyncResult | null;
}

interface ServerSyncResult {
  updatedCount: number;
  failedCount: number;
  lastSyncTime: string;
}

export const useSyncWorkItemCalendar = (): SyncStatusResult => {
  const dispatch = useDispatch();
  // Använd useRef för att spåra senaste synkroniseringstiden
  const lastSyncTimeRef = useRef<number>(0);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  // State för synkroniseringsresultat
  const [lastSyncResult, setLastSyncResult] =
    useState<WorkItemSyncResult | null>(null);
  const [lastServerSyncResult, setLastServerSyncResult] =
    useState<ServerSyncResult | null>(null);

  // API hooks
  const [
    getDeltaKey,
    { isLoading: isLoadingDeltaToken, error: deltaKeyError },
  ] = useLazyGetMsGraphDeltaKeyQuery();
  const [postDeltaKey] = usePostMsGraphDeltaKeyMutation();
  const [triggerSync, { isLoading: isLoadingCalendar, error: calendarError }] =
    useLazySyncWorkItemCalendarEventsQuery();
  const [
    updateWorkItems,
    { isLoading: isUpdatingWorkItems, error: updateError },
  ] = useUpdateWorkItemsFromCalendarMutation();

  // Kombinerad laddningsstatus och fel
  const isLoading =
    isLoadingCalendar || isUpdatingWorkItems || isLoadingDeltaToken;
  const error = calendarError || updateError || deltaKeyError;

  // Funktion för att processa kalendersynkroniseringsdata
  const processCalendarSync = useCallback(
    async (data: WorkItemSyncResult) => {
      // Spara resultatet
      setLastSyncResult(data);

      // Kontrollera om det finns ändringar att synkronisera
      const hasChanges =
        data.added.length > 0 ||
        data.modified.length > 0 ||
        data.deleted.length > 0;

      if (!hasChanges) {
        console.log("Inga kalenderhändelser att synkronisera");
        return;
      }

      // Logga ändringar
      console.log("Kalenderhändelser att synkronisera:", {
        added: data.added.length,
        modified: data.modified.length,
        deleted: data.deleted.length,
      });

      // Alltid refresha kalendervyn när delta-synken hittar ändringar
      dispatch(msBaseApi.util.invalidateTags(["Calendar"]));

      // Uppdatera workItems på servern
      await updateWorkItems({
        added: data.added,
        modified: data.modified,
        deleted: data.deleted,
      })
        .unwrap()
        .then((response) => {
          const result = response;
          console.log("WorkItems synkroniserade:", result);
          if (result.UpdatedWorkItems?.length > 0) {
            dispatch(baseApi.util.invalidateTags(["Workitems", "MyCustomer"]));
            console.log("Data refreshad efter synkronisering");
          }
          setLastServerSyncResult({
            updatedCount: result.UpdatedWorkItems?.length || 0,
            failedCount: result.FailedWorkItems?.length || 0,
            lastSyncTime: new Date().toISOString(),
          });
        })
        .catch((error) => {
          console.error("Oväntat fel vid synkronisering:", error);
          throw new Error("Oväntat fel vid synkronisering");
        });
    },
    [dispatch, updateWorkItems]
  );

  const syncWithDeltaToken = useCallback(
    async (token: string | undefined) => {
      // Throttling-kontroll
      const now = Date.now();
      if (now - lastSyncTimeRef.current < SYNC_THROTTLE_MS) {
        console.log("Delta-synkronisering skippas (throttling)");
        return;
      }

      lastSyncTimeRef.current = now;
      console.log("Utför delta-synkronisering med token:", token);

      try {
        const data = await triggerSync({ deltaToken: token }).unwrap();

        if (data) {
          console.log("Delta-synkronisering lyckades");

          // Process data om vi hade en token (inte initial request)
          if (token && token.length > 0) {
            await processCalendarSync(data);
          }
          // Spara deltaLink om tillgänglig
          if (data.deltaLink) {
            await postDeltaKey(data.deltaLink).catch((error) => {
              console.error("Fel vid sparande av deltaLink:", error);
            });
          }
        }
      } catch (error) {
        console.error("Fel vid delta-synkronisering:", error);
      }
    },
    [triggerSync, processCalendarSync, postDeltaKey]
  );

  // Initial synkronisering och uppsättning av intervaller
  useEffect(() => {
    let isMounted = true;
    const initializeSync = async () => {
      try {
        const response = await getDeltaKey().unwrap();
        if (!isMounted) return;

        console.log("Hämtar delta-token:", response);
        await syncWithDeltaToken(response);

        // Sätt upp intervall för periodisk synkronisering
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
        }

        intervalIdRef.current = setInterval(async () => {
          if (!isMounted) return;
          console.log("Periodisk synkronisering startas...");
          try {
            const token = await getDeltaKey().unwrap();
            if (!isMounted) return;
            await syncWithDeltaToken(token);
          } catch (error) {
            console.error(
              "Fel vid hämtning av token för periodisk synkronisering:",
              error
            );
          }
        }, SYNC_INTERVAL_MS);

        // Synkronisera när fönstret får fokus
        const handleFocus = async () => {
          if (!isMounted) return;
          console.log("Fönstret fick fokus, synkroniserar kalender...");
          try {
            const token = await getDeltaKey().unwrap();
            if (!isMounted) return;
            await syncWithDeltaToken(token);
          } catch (error) {
            console.error("Fel vid fokus-synkronisering:", error);
          }
        };

        window.addEventListener("focus", handleFocus);

        return () => {
          isMounted = false;
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          window.removeEventListener("focus", handleFocus);
        };
      } catch (error) {
        console.error("Fel vid initial synkronisering:", error);
      }
    };

    // Starta initialiseringen
    initializeSync();

    // Cleanup funktion vid unmounting
    return () => {
      isMounted = false;
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [getDeltaKey, syncWithDeltaToken]);

  return {
    isLoading,
    error,
    lastSyncResult,
    lastServerSyncResult,
  };
};
